import { OrderDetail } from '@features/orders/models/order-detail.model';

enum PaymentMethod {
  'CREDIT_DEBIT',
  'APP_AGORA',
  'OH_PAY',
  'OH',
  'INTERBANK',
  'CASH',
  'KIOSKO',
  'CUOTEALO',
  'ACUOTAZ',
  'SIP',
  'OTHER',
}

interface PaymentOptions {
  [PaymentMethod.CREDIT_DEBIT]: (
    tenantId: string,
    orderDetail: OrderDetail,
    cdnAssetsUrl: string
  ) => PaymentData;
  [PaymentMethod.APP_AGORA]: (
    tenantId: string,
    orderDetail: OrderDetail,
    cdnAssetsUrl: string
  ) => PaymentData;
  [PaymentMethod.OH_PAY]: (
    tenantId: string,
    orderDetail: OrderDetail,
    cdnAssetsUrl: string
  ) => PaymentData;
  [PaymentMethod.OH]: (
    tenantId: string,
    orderDetail: OrderDetail,
    cdnAssetsUrl: string
  ) => PaymentData;
  [PaymentMethod.INTERBANK]: (
    tenantId: string,
    orderDetail: OrderDetail,
    cdnAssetsUrl: string
  ) => PaymentData;
  [PaymentMethod.CASH]: (
    tenantId: string,
    orderDetail: OrderDetail,
    cdnAssetsUrl: string
  ) => PaymentData;
  [PaymentMethod.KIOSKO]: (
    tenantId: string,
    orderDetail: OrderDetail,
    cdnAssetsUrl: string
  ) => PaymentData;
  [PaymentMethod.CUOTEALO]: (
    tenantId: string,
    orderDetail: OrderDetail,
    cdnAssetsUrl: string
  ) => PaymentData;
  [PaymentMethod.ACUOTAZ]: (
    tenantId: string,
    orderDetail: OrderDetail,
    cdnAssetsUrl: string
  ) => PaymentData;
  [PaymentMethod.SIP]: (
    tenantId: string,
    orderDetail: OrderDetail,
    cdnAssetsUrl: string
  ) => PaymentData;
  [PaymentMethod.OTHER]: (
    tenantId: string,
    orderDetail: OrderDetail,
    cdnAssetsUrl: string
  ) => PaymentData;
}

interface PaymentData {
  name: string;
  showName: boolean;
  digits: string;
  iconUrl: string;
}

function getPaymentMethod(paymentSystemName: string): PaymentMethod {
  let lowerPaymentSystemName = paymentSystemName.toLowerCase().trim();
  lowerPaymentSystemName = lowerPaymentSystemName
    .replaceAll(/\s/g, '-')
    .replaceAll(/á/g, 'a')
    .replaceAll(/é/g, 'e')
    .replaceAll(/í/g, 'i')
    .replaceAll(/ó/g, 'o')
    .replaceAll(/ú/g, 'u');

  switch (lowerPaymentSystemName) {
    case 'visa':
    case 'mastercard':
    case 'american-express':
    case 'diners':
    case 'otras-tarjetas-de-credito-y-debito':
    case 'tarjetas-credito-debito-prom':
      return PaymentMethod.CREDIT_DEBIT;
    case 'app-agora':
    case 'agoraweb':
      return PaymentMethod.APP_AGORA;
    case 'tarjeta-de-debito-oh!pay':
    case 'agora-visa':
    case 'tarjeta-de-debito-sip':
      return PaymentMethod.OH_PAY;
    case 'tarjeta-de-credito-oh':
    case 'tarjeta-de-credito-oh!':
    case 'tarjeta-oh-cuotas':
    case 'tarjeta-oh!-visa-y-mastercard':
    case 'tarjeta-de-credito-sip':
      return PaymentMethod.OH;
    case 'tarjeta-interbank-o-dividelo':
    case 'tarjetas-interbank':
      return PaymentMethod.INTERBANK;
    case 'transferencia-bancaria/pago-en-efectivo':
    case 'safetypay':
    case 'loterica':
      return PaymentMethod.CASH;
    case 'paga-en-kioskos':
      return PaymentMethod.KIOSKO;
    case 'cuotealo':
      return PaymentMethod.CUOTEALO;
    case 'acuotaz':
      return PaymentMethod.ACUOTAZ;
    case 'tarjeta-sip':
      return PaymentMethod.SIP;
    default:
      return PaymentMethod.OTHER;
  }
}

const paymentOptions: PaymentOptions = {
  [PaymentMethod.CREDIT_DEBIT]: (
    tenantId: string,
    orderDetail: OrderDetail,
    cdnAssetsUrl: string
  ): PaymentData => {
    return {
      showName: true,
      get name(): string {
        return 'Tarjeta de crédito o débito';
      },
      get digits(): string {
        return orderDetail.paymentDigitCard
          ? `**** **** **** ${orderDetail.paymentDigitCard}`
          : '';
      },
      get iconUrl(): string {
        if (tenantId === 'HPSA') {
          return `${cdnAssetsUrl}/images/payments/hpsa-card.png`;
        } else if (tenantId === 'PVEA') {
          return `${cdnAssetsUrl}/images/payments/pvea-card.png`;
        } else if (tenantId === 'RPLAZA') {
          return `${cdnAssetsUrl}/images/payments/rplaza-card.png`;
        } else if (tenantId === 'TPSA') {
          return `${cdnAssetsUrl}/images/payments/tpsa-card.png`;
        }

        return '';
      },
    };
  },
  [PaymentMethod.APP_AGORA]: (
    tenantId: string,
    orderDetail: OrderDetail,
    cdnAssetsUrl: string
  ): PaymentData => {
    return {
      showName: false,
      get name(): string {
        return 'App Agora';
      },
      get digits(): string {
        return orderDetail.paymentDigitCard
          ? `**** **** **** ${orderDetail.paymentDigitCard}`
          : '';
      },
      get iconUrl(): string {
        return `${cdnAssetsUrl}/images/payments/agora.png`;
      },
    };
  },
  [PaymentMethod.OH_PAY]: (
    tenantId: string,
    orderDetail: OrderDetail,
    cdnAssetsUrl: string
  ): PaymentData => {
    return {
      showName: true,
      get name(): string {
        return 'Tarjeta de débito';
      },
      get digits(): string {
        return orderDetail.paymentDigitCard
          ? `**** **** **** ${orderDetail.paymentDigitCard}`
          : '';
      },
      get iconUrl(): string {
        return `${cdnAssetsUrl}/images/payments/sip-debito.png`;
      },
    };
  },
  [PaymentMethod.OH]: (
    tenantId: string,
    orderDetail: OrderDetail,
    cdnAssetsUrl: string
  ): PaymentData => {
    return {
      showName: true,
      get name(): string {
        return 'Tarjeta de crédito';
      },
      get digits(): string {
        return orderDetail.paymentDigitCard
          ? `**** **** **** ${orderDetail.paymentDigitCard}`
          : '';
      },
      get iconUrl(): string {
        return `${cdnAssetsUrl}/images/payments/sip-credito.png`;
      },
    };
  },
  [PaymentMethod.INTERBANK]: (
    tenantId: string,
    orderDetail: OrderDetail,
    cdnAssetsUrl: string
  ): PaymentData => {
    return {
      showName: true,
      get name(): string {
        return 'Tarjeta Interbank';
      },
      get digits(): string {
        return orderDetail.paymentDigitCard
          ? `**** **** **** ${orderDetail.paymentDigitCard}`
          : '';
      },
      get iconUrl(): string {
        return `${cdnAssetsUrl}/images/payments/interbank.png`;
      },
    };
  },
  [PaymentMethod.CASH]: (
    tenantId: string,
    orderDetail: OrderDetail,
    cdnAssetsUrl: string
  ): PaymentData => {
    return {
      showName: true,
      get name(): string {
        return 'Transferencia/Efectivo';
      },
      get digits(): string {
        return orderDetail.paymentDigitCard
          ? `**** **** **** ${orderDetail.paymentDigitCard}`
          : '';
      },
      get iconUrl(): string {
        if (tenantId === 'HPSA') {
          return `${cdnAssetsUrl}/images/payments/hpsa-cash.png`;
        } else if (tenantId === 'PVEA') {
          return `${cdnAssetsUrl}/images/payments/pvea-cash.png`;
        } else if (tenantId === 'RPLAZA') {
          return `${cdnAssetsUrl}/images/payments/rplaza-cash.png`;
        } else if (tenantId === 'TPSA') {
          return `${cdnAssetsUrl}/images/payments/tpsa-cash.png`;
        }

        return '';
      },
    };
  },
  [PaymentMethod.KIOSKO]: (
    tenantId: string,
    orderDetail: OrderDetail,
    cdnAssetsUrl: string
  ): PaymentData => {
    return {
      showName: true,
      get name(): string {
        return 'Kiosko';
      },
      get digits(): string {
        return orderDetail.paymentDigitCard
          ? `**** **** **** ${orderDetail.paymentDigitCard}`
          : '';
      },
      get iconUrl(): string {
        if (tenantId === 'TPSA') {
          return `${cdnAssetsUrl}/images/payments/tpsa-kioko.png`;
        }

        return '';
      },
    };
  },
  [PaymentMethod.CUOTEALO]: (
    tenantId: string,
    orderDetail: OrderDetail,
    cdnAssetsUrl: string
  ): PaymentData => {
    return {
      showName: false,
      get name(): string {
        return 'Cuotéalo';
      },
      get digits(): string {
        return orderDetail.paymentDigitCard
          ? `**** **** **** ${orderDetail.paymentDigitCard}`
          : '';
      },
      get iconUrl(): string {
        return `${cdnAssetsUrl}/images/payments/cuotealo.png`;
      },
    };
  },
  [PaymentMethod.ACUOTAZ]: (
    tenantId: string,
    orderDetail: OrderDetail,
    cdnAssetsUrl: string
  ): PaymentData => {
    return {
      showName: false,
      get name(): string {
        return 'aCuotaz';
      },
      get digits(): string {
        return orderDetail.paymentDigitCard
          ? `**** **** **** ${orderDetail.paymentDigitCard}`
          : '';
      },
      get iconUrl(): string {
        return `${cdnAssetsUrl}/images/payments/a-cuotaz.png`;
      },
    };
  },
  [PaymentMethod.SIP]: (
    tenantId: string,
    orderDetail: OrderDetail,
    cdnAssetsUrl: string
  ): PaymentData => {
    return {
      showName: true,
      get name(): string {
        return 'Tarjeta Sip';
      },
      get digits(): string {
        return orderDetail.paymentDigitCard
          ? `**** **** **** ${orderDetail.paymentDigitCard}`
          : '';
      },
      get iconUrl(): string {
        return `${cdnAssetsUrl}/images/payments/sip-debito-v1.png`;
      },
    };
  },
  [PaymentMethod.OTHER]: (
    tenantId: string,
    orderDetail: OrderDetail,
    cdnAssetsUrl: string
  ): PaymentData => {
    return {
      showName: true,
      get name(): string {
        return orderDetail.paymentMethod;
      },
      get digits(): string {
        return orderDetail.paymentDigitCard
          ? `**** **** **** ${orderDetail.paymentDigitCard}`
          : '';
      },
      get iconUrl(): string {
        return '';
      },
    };
  },
};

export default function getPaymentData(
  tenantId: string,
  orderDetail: any,
  cdnAssetsUrl: string
): PaymentData {
  const paymentMethod = getPaymentMethod(orderDetail.paymentMethod);
  return paymentOptions[paymentMethod](tenantId, orderDetail, cdnAssetsUrl);
}

export type { PaymentData };

// SE DEBE OPTIMIZAR
